// frontend/src/services/auth.js
import { API_BASE } from "../config/api";
import { httpRequest } from "../utils/httpClient";
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import logger from '../utils/logger';

// In-memory token cache to bridge async Capacitor restore with sync getToken() calls
let _cachedToken = null;
let _cachedExpires = null;

function _syncTokenToLocal(token, expires) {
  if (token) {
    _cachedToken = token;
    localStorage.setItem("token", token);
  }
  if (expires) {
    _cachedExpires = expires;
    localStorage.setItem("token_expires", expires);
  }
}

function _clearLocalToken() {
  _cachedToken = null;
  _cachedExpires = null;
  localStorage.removeItem("token");
  localStorage.removeItem("token_expires");
}

// Storage helper that works on both web and mobile
const storageHelper = {
  async getItem(key) {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      return localStorage.getItem(key);
    }
  },

  async setItem(key, value) {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  },

  async removeItem(key) {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  }
};

export const authService = {
  // Read token from in-memory cache first, then localStorage (sync, immediate access)
  getToken() {
    return _cachedToken || localStorage.getItem("token");
  },

  // Restore token from Capacitor storage on mobile app startup (async init only)
  async restoreFromCapacitor() {
    if (Capacitor.isNativePlatform()) {
      try {
        const { value: token } = await Preferences.get({ key: 'token' });
        const { value: tokenExpires } = await Preferences.get({ key: 'token_expires' });

        if (token) {
          // Restore to localStorage and in-memory cache for immediate access
          _syncTokenToLocal(token, tokenExpires);
          return token;
        }
      } catch (error) {
        logger.error('[Auth] Failed to restore from Capacitor storage:', error);
      }
    }
    return null;
  },

  setToken(token, rememberMe = false) {
    const expires = rememberMe ?
      (Date.now() + 30 * 24 * 60 * 60 * 1000).toString() :
      (Date.now() + 7 * 24 * 60 * 60 * 1000).toString();

    // Store in localStorage and in-memory cache for immediate access
    _syncTokenToLocal(token, expires);

    if (Capacitor.isNativePlatform()) {
      // Also store in Capacitor storage for persistence (async, fire and forget)
      storageHelper.setItem("token", token);
      storageHelper.setItem("token_expires", expires);
    }
  },

  isTokenExpired() {
    const expires = _cachedExpires || localStorage.getItem("token_expires");
    if (!expires) return true;
    return Date.now() > parseInt(expires, 10);
  },

  removeToken() {
    // Always remove from localStorage and in-memory cache
    _clearLocalToken();

    if (Capacitor.isNativePlatform()) {
      // Also remove from Capacitor storage (async, fire and forget)
      storageHelper.removeItem("token");
      storageHelper.removeItem("token_expires");
    }

    // Clear Quick Start sessionStorage flags so modal shows on next login
    try {
      sessionStorage.removeItem("quick_start_shown_this_session");
      sessionStorage.removeItem("quick_start_state");
    } catch (e) {
      // Ignore sessionStorage errors
    }
  },

  getAuthHeaders() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  async register(email, password, rememberMe = false, legalVersion = null) {
    const payload = { email, password };
    if (legalVersion) {
      payload.legal_version = legalVersion;
    }
    const res = await httpRequest(`${API_BASE}/auth/email/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, remember_me: rememberMe }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Registration failed");
    }
    const data = await res.json();
    // Email/password registrations now require verification before full login.
    if (data.access_token) {
      await this.setToken(data.access_token, rememberMe);
    }
    return data;
  },

  async login(email, password, rememberMe = false) {
    const loginUrl = `${API_BASE}/auth/email/login`;
    const requestHeaders = { "Content-Type": "application/json" };
    const requestBody = JSON.stringify({ email, password, remember_me: rememberMe });


    try {
      const res = await httpRequest(loginUrl, {
        method: "POST",
        headers: requestHeaders,
        body: requestBody,
      });


      if (!res.ok) {
        let errorData;
        try {
          errorData = await res.json();
        } catch (e) {
          errorData = { detail: `HTTP ${res.status}: ${res.statusText}` };
        }
                throw new Error(errorData.detail || "Login failed");
      }
      const data = await res.json();
      if (data.access_token) {
        await this.setToken(data.access_token, rememberMe);
      }
      return data;
    } catch (err) {
            throw err;
    }
  },

  async oauthCallback(provider, token, email = null, name = null, rememberMe = false) {
    const url = `${API_BASE}/auth/oauth/callback`;

    try {
      const res = await httpRequest(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider,
          token,
          email,
          name,
          remember_me: rememberMe,
        }),
      });


      if (!res.ok) {
        let errorData;
        try {
          errorData = await res.json();
        } catch (e) {
          errorData = { detail: `HTTP ${res.status}: ${res.statusText}` };
        }
                throw new Error(errorData.detail || "OAuth authentication failed");
      }

      const data = await res.json();
            await this.setToken(data.access_token, rememberMe);
      return data;
    } catch (err) {

      // Handle network errors (Failed to Fetch or CapacitorHttp network errors)
      if (err.message === "Failed to fetch" || err.name === "TypeError" || err.message.includes("fetch") || err.isNetworkError) {
        logger.error(`Network error connecting to backend at ${url}`);
        logger.error("Make sure the backend is running on", API_BASE);
        throw new Error(
          `Cannot connect to backend server at ${API_BASE}. ` +
          `Please ensure the backend is running and accessible. ` +
          `For local development, start the backend with: python -m uvicorn backend.main:app --reload`
        );
      }
      // Re-throw other errors
      throw err;
    }
  },

  async getCurrentUser() {
    const res = await httpRequest(`${API_BASE}/auth/me`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) {
      return null;
    }
    return await res.json();
  },

  async resendVerificationEmail(email) {
    const res = await httpRequest(`${API_BASE}/auth/email/resend-verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to resend verification email");
    }
    return await res.json();
  },

  async requestPasswordReset(email) {
    const res = await httpRequest(`${API_BASE}/auth/password/forgot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to send password reset email");
    }
    return await res.json();
  },

  async resetPassword(token, password) {
    const res = await httpRequest(`${API_BASE}/auth/password/reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to reset password");
    }
    return await res.json();
  },

  async verifyEmailToken(token) {
    const res = await httpRequest(`${API_BASE}/auth/email/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Email verification failed");
    }
    const data = await res.json();
    if (data.access_token) {
      await this.setToken(data.access_token);
    }
    return data;
  },

  async getAuthStatus() {
    const res = await httpRequest(`${API_BASE}/auth/status`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) {
      return { is_authenticated: false, is_email_verified: false };
    }
    return await res.json();
  },

  async updateUsername(username) {
    const res = await httpRequest(`${API_BASE}/profile/username`, {
      method: "PUT",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to update username");
    }
    return await res.json();
  },

  async updateBggId(bgg_id) {
    const res = await httpRequest(`${API_BASE}/profile/bgg-id`, {
      method: "PUT",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ bgg_id }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to update BGG ID");
    }
    return await res.json();
  },

  async updateCountry(country_of_domicile) {
    const res = await httpRequest(`${API_BASE}/profile/country`, {
      method: "PUT",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ country_of_domicile }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to update country");
    }
    return await res.json();
  },

  async updatePersona(persona_title, persona_icon) {
    const res = await httpRequest(`${API_BASE}/auth/persona`, {
      method: "PATCH",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ persona_title, persona_icon }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to save persona");
    }
    return await res.json();
  },

  async deleteAccount() {
    const res = await httpRequest(`${API_BASE}/profile/account`, {
      method: "DELETE",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to request account deletion");
    }
    return await res.json();
  },

  async reactivateAccount(token) {
    const res = await httpRequest(`${API_BASE}/profile/account/reactivate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to reactivate account");
    }
    const data = await res.json();
    if (data.access_token) {
      await this.setToken(data.access_token);
    }
    return data;
  },

  async logout() {
    try {
      await httpRequest(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      logger.error("[Auth] Backend logout failed:", error);
    }
    this.removeToken();

    // Clear Quick Start sessionStorage flags so modal shows on next login
    try {
      sessionStorage.removeItem("quick_start_shown_this_session");
      sessionStorage.removeItem("quick_start_state");
      logger.info("[Auth] Cleared Quick Start sessionStorage flags on logout");
    } catch (e) {
      // Ignore sessionStorage errors
    }
  },
};
